import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "../primitives";
import { mcpClient } from "../../../lib/mcp-client";
import {
  useSetWrappedApiCredential,
  useSetWrappedApiServiceCredential,
  type CredentialPayload,
  type WrappedApiAuthKind,
} from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16a/16b — credential form for a wrapped API. The shape depends on
 * auth_kind. Credentials are write-only — never displayed back. Sprint 16b adds:
 *   - an OAuth Authorise flow (popup → provider consent → postMessage callback)
 *   - a `serviceAccount` mode that writes the shared workspace credential
 */
export function CredentialEditor({
  slug,
  authKind,
  credentialSet,
  credentialUsername = null,
  serviceAccount = false,
  oauthConfigured = false,
}: {
  slug: string;
  authKind: WrappedApiAuthKind;
  credentialSet: boolean;
  /** Sprint 16f follow-up: non-secret username of the stored basic-auth credential
   *  (never the password) so the form isn't blank after setup. */
  credentialUsername?: string | null;
  serviceAccount?: boolean;
  oauthConfigured?: boolean;
}) {
  const qc = useQueryClient();
  const setUserCredential = useSetWrappedApiCredential();
  const setServiceCredential = useSetWrappedApiServiceCredential();
  const setCredential = serviceAccount ? setServiceCredential : setUserCredential;

  const [authorizing, setAuthorizing] = useState(false);
  const popupRef = useRef<Window | null>(null);

  // Bearer
  const [token, setToken] = useState("");
  // API key
  const [key, setKey] = useState("");
  const [keyLocation, setKeyLocation] = useState<"header" | "query">("header");
  const [keyName, setKeyName] = useState("X-API-Key");
  // Basic
  const [username, setUsername] = useState(credentialUsername ?? "");
  const [password, setPassword] = useState("");

  // Sprint 16f follow-up: show the stored username once it loads (write-only
  // form — the password is never returned, only a placeholder indicates one is
  // set). Re-syncs after a save invalidates the query.
  useEffect(() => {
    setUsername(credentialUsername ?? "");
  }, [credentialUsername, slug]);

  // OAuth callback listener — the popup posts back wrapped_oauth_complete/error.
  useEffect(() => {
    if (!authorizing) return;
    function onMessage(e: MessageEvent) {
      const data = e.data as { type?: string; slug?: string; error?: string };
      if (data?.type === "wrapped_oauth_complete" && data.slug === slug) {
        setAuthorizing(false);
        showToast("Authorized");
        qc.invalidateQueries({ queryKey: ["wrapped-api", slug] });
        qc.invalidateQueries({ queryKey: ["wrapped-apis"] });
      } else if (data?.type === "wrapped_oauth_error" && data.slug === slug) {
        setAuthorizing(false);
        showToast(data.error ?? "Authorization failed", "error", true);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [authorizing, slug, qc]);

  async function handleAuthorize() {
    try {
      setAuthorizing(true);
      const url = await mcpClient.getWrappedApiAuthorizeUrl(slug);
      popupRef.current = window.open(url, "wrapped_oauth", "width=620,height=760");
      if (!popupRef.current) {
        setAuthorizing(false);
        showToast("Popup blocked — allow popups and retry.", "error", true);
      }
    } catch (err) {
      setAuthorizing(false);
      showToast(err instanceof Error ? err.message : "Couldn't start authorization", "error", true);
    }
  }

  // OAuth Authorization Code — Authorise button instead of a credential form.
  if (authKind === "oauth2_auth_code") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <StatusLine set={credentialSet} setLabel="authorized" unsetLabel="not authorized" />
        {!oauthConfigured ? (
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
            The owner must finish OAuth setup before you can authorize.
          </div>
        ) : (
          <div>
            <button type="button" onClick={handleAuthorize} disabled={authorizing} style={saveBtn}>
              {authorizing ? "Waiting for provider…" : credentialSet ? "Re-authorize" : "Authorize"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // OAuth Client Credentials — no per-user step.
  if (authKind === "oauth2_client_credentials") {
    return (
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
        This API uses the OAuth Client Credentials grant — no per-user authorization is needed. Calls use the
        owner-configured client credentials.
      </div>
    );
  }

  function buildPayload(): CredentialPayload | { error: string } {
    if (authKind === "bearer") {
      if (!token.trim()) return { error: "Token is required." };
      return { token: token.trim() };
    }
    if (authKind === "api_key") {
      if (!key.trim()) return { error: "Key is required." };
      if (!keyName.trim()) return { error: "Header/param name is required." };
      return { key: key.trim(), in: keyLocation, name: keyName.trim() };
    }
    if (!username.trim() || !password) return { error: "Username and password are required." };
    return { username: username.trim(), password };
  }

  async function handleSave() {
    const payload = buildPayload();
    if ("error" in payload) {
      showToast(payload.error, "error", true);
      return;
    }
    try {
      await setCredential.mutateAsync({ wrapper_slug: slug, credential_payload: payload });
      showToast(serviceAccount ? "Service credential saved" : "Credential saved");
      setToken("");
      setKey("");
      setUsername("");
      setPassword("");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't save credential", "error", true);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <StatusLine set={credentialSet} setLabel="set" unsetLabel="not set" />

      {authKind === "bearer" && (
        <Field label="Bearer token">
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="paste token" style={inputStyle} autoComplete="off" />
        </Field>
      )}

      {authKind === "api_key" && (
        <>
          <Field label="API key">
            <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="paste key" style={inputStyle} autoComplete="off" />
          </Field>
          <div style={{ display: "flex", gap: 10 }}>
            <Field label="Sent as">
              <select value={keyLocation} onChange={(e) => setKeyLocation(e.target.value as "header" | "query")} style={inputStyle}>
                <option value="header">Header</option>
                <option value="query">Query param</option>
              </select>
            </Field>
            <Field label="Name">
              <input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="X-API-Key" style={inputStyle} />
            </Field>
          </div>
        </>
      )}

      {authKind === "basic" && (
        <>
          <Field label="Username">
            <input value={username} onChange={(e) => setUsername(e.target.value)} style={inputStyle} autoComplete="off" />
          </Field>
          <p style={hintStyle}>
            Some APIs need a special username — e.g. <strong>Zendesk</strong> uses{" "}
            <code style={codeStyle}>email/token</code> with an API token as the password.
          </p>
          <Field label="Password / token">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              autoComplete="off"
              placeholder={credentialSet ? "•••••••• — enter a new value to replace" : undefined}
            />
          </Field>
          {credentialSet && (
            <p style={hintStyle}>
              For security, the saved password/token is never shown. Re-enter it (with the username) to replace the credential.
            </p>
          )}
        </>
      )}

      <div>
        <button type="button" onClick={handleSave} disabled={setCredential.isPending} style={saveBtn}>
          {setCredential.isPending ? "Saving…" : credentialSet ? "Replace credential" : "Save credential"}
        </button>
      </div>
    </div>
  );
}

function StatusLine({ set, setLabel, unsetLabel }: { set: boolean; setLabel: string; unsetLabel: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
        Status:{" "}
        <strong style={{ color: set ? "var(--c-green-800, var(--color-text-primary))" : "var(--color-text-primary)" }}>
          {set ? setLabel : unsetLabel}
        </strong>
        .
      </span>
      {set && (
        <span style={pill} title="Stored encrypted and never shown again">
          <i className="ti ti-lock" style={{ fontSize: 11 }} aria-hidden="true" /> encrypted
        </span>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const hintStyle: CSSProperties = {
  fontSize: 11.5,
  lineHeight: 1.45,
  color: "var(--color-text-tertiary)",
  margin: "-4px 0 0",
};

const codeStyle: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 11,
  padding: "0 3px",
  borderRadius: 3,
  background: "var(--color-background-secondary)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};

const saveBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const pill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: 10,
  color: "var(--c-green-800, var(--color-text-secondary))",
  background: "var(--c-green-50, var(--color-background-secondary))",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "1px 6px",
};
